/**
 * Single region-display formatter for the SurfTime desktop tree (021
 * fix-wave, gate defect #5/H7; promoted to `lib/` in the #77 follow-up).
 *
 * Before the original fix, `FavoritesHorizonRow.tsx` printed `beach.region`
 * RAW ("LOS_ANGELES"), `PickOfDayHero.tsx`/`FiringStrip.tsx` lowercased the
 * underscore-replaced key ("los angeles"), `SurfSearch.tsx` had its own
 * Title-Case formatter, and `Home.tsx`/`BeachList.tsx`/`BeachCard.tsx` each
 * carried an independently hand-typed `REGION_LABELS` copy that had already
 * drifted — Home.tsx said "Ventura & Santa Barbara" while BeachList/
 * BeachCard said "Ventura / Santa Barbara" for the SAME region. Every
 * desktop region render inside `apps/surftime/` routes through
 * `formatRegionLabel` below.
 *
 * Lives in `lib/` (a genuinely platform-neutral location, not `apps/
 * surftime/`) specifically so `components/launcher/BeachesWidget.tsx` can
 * import the canonical map too — the launcher's static import graph must
 * NEVER reach into an app's own tree (`App.tsx`'s header: "none of them
 * may be reachable from the login page or launcher's static import graph,
 * since SurfTime/LocalTea/BitzBuggy pull in recharts + maplibre-gl"), so a
 * shared table living inside `apps/surftime/utils/` was never safely
 * importable from the launcher — this file has zero imports of its own, so
 * it can't accidentally drag any app-only dependency into the launcher's
 * eager bundle. `BeachesWidget.tsx` composes its own compact override for
 * `ventura_santa_barbara` ("Ventura · SB") on top of this map — a
 * deliberate, narrower label for its tight horizontal space, not
 * duplicated drift.
 */

/** Canonical region key -> display label (api-contract.md region enum). */
export const REGION_LABELS: Record<string, string> = {
  san_diego: 'San Diego',
  orange_county: 'Orange County',
  los_angeles: 'Los Angeles',
  ventura_santa_barbara: 'Ventura / Santa Barbara',
  central_coast: 'Central Coast',
  monterey_bay: 'Monterey Bay',
  san_francisco: 'San Francisco',
  north_coast: 'North Coast',
};

/** "some_new_region" -> "Some new region" — fallback for a region key the
 *  map above doesn't (yet) carry, so an unmapped region still reads as
 *  prose instead of a raw snake_case wire value. */
function titleCaseFallback(region: string): string {
  return region
    .split('_')
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** Format a region key for display. `null`/`undefined`/empty resolve to
 *  `''` so callers can compose it freely (e.g. `${region} · ${breakType}`)
 *  without a stray "undefined" appearing. */
export function formatRegionLabel(region: string | null | undefined): string {
  if (!region) return '';
  return REGION_LABELS[region] ?? titleCaseFallback(region);
}
